import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { useKeyboardShortcuts, useSequenceShortcut } from '../hooks/useKeyboardShortcuts';
import ShortcutsHelpDialog from './ShortcutsHelpDialog';
import AppFooter from './AppFooter';
import { getVersionString } from '../config/version';
import {
  LayoutDashboard,
  BookOpen,
  Receipt,
  DollarSign,
  FileBarChart,
  TrendingUp,
  List,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
  Menu,
  X,
  Settings,
  FileText,
  Archive,
  Users,
  Truck,
  Package,
  Ruler,
  Ship,
  ShoppingCart,
  PanelLeftClose,
  PanelLeft,
  Calendar,
  Lock,
  Unlock,
  Upload,
  RotateCcw,
  Monitor,
  UserCheck,
  ClipboardList,
  AlertTriangle,
  Stethoscope,
  HeartPulse,
  BookMarked,
  ScanLine,
  BarChart3,
  Pill
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { getRoleDisplayName } from '../lib/utils';
import LanguageSwitcher from './LanguageSwitcher';

// Ocean Fishing Bissau Logo Component
const KairosLogo = ({ size = 'md' }) => {
  const sizes = {
    sm: { wrapper: 'w-8 h-8' },
    md: { wrapper: 'w-10 h-10' },
    lg: { wrapper: 'w-12 h-12' }
  };
  const s = sizes[size];
  
  return (
    <div className={`${s.wrapper} relative flex-shrink-0`}>
      <img 
        src="/ocean-fishing-logo.png" 
        alt="Ocean Fishing Bissau" 
        className="w-full h-full object-contain"
      />
    </div>
  );
};

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, canEdit, canAdmin, isSuperAdmin, isCashier, currentOrg } = useAuth();

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/chart-of-accounts', icon: BookOpen, label: t('nav.chartOfAccounts') },
    { path: '/vouchers', icon: Receipt, label: t('nav.voucherEntry'), requiresEdit: true },
    { path: '/cr-db-notes', icon: FileText, label: t('nav.crDbNotes'), requiresEdit: true },
    { path: '/sales-quotations', icon: FileText, label: t('nav.salesQuotations'), requiresEdit: true },
    { path: '/sales-invoices', icon: ShoppingCart, label: t('nav.salesInvoices'), requiresEdit: true },
    { path: '/fishing-trips', icon: Ship, label: t('nav.oceanFishingTrip'), requiresEdit: true },
    { path: '/sales-returns', icon: RotateCcw, label: t('nav.salesReturns'), requiresEdit: true },
    { path: '/purchase-invoices', icon: Truck, label: t('nav.purchaseInvoices'), requiresEdit: true },
    { path: '/purchase-orders', icon: ClipboardList, label: t('nav.purchaseOrders'), requiresEdit: true },
    { path: '/purchase-returns', icon: RotateCcw, label: t('nav.purchaseReturns'), requiresEdit: true },
    { path: '/customers', icon: Users, label: t('nav.customers') },
    { path: '/suppliers', icon: Truck, label: t('nav.suppliers') },
    { path: '/inventory', icon: Package, label: t('nav.inventory') },
    { path: '/calibres', icon: Ruler, label: t('nav.calibres') },
    { path: '/exchange-rates', icon: DollarSign, label: t('nav.exchangeRates') },
    { path: '/trial-balance', icon: FileBarChart, label: t('nav.trialBalance') },
    { path: '/general-ledger', icon: List, label: t('nav.generalLedger') },
    { path: '/journal', icon: BookOpen, label: t('nav.journal') },
    { path: '/sales-analytics', icon: BarChart3, label: t('nav.salesAnalytics'), requiresSuperAdmin: true },
    { path: '/income-statement', icon: TrendingUp, label: t('nav.incomeStatement') },
    { path: '/import-data', icon: Upload, label: t('nav.importData'), requiresSuperAdmin: true },
    { path: '/settings', icon: Settings, label: t('nav.settings'), requiresSuperAdmin: true },
  ];

  const filteredItems = navItems.filter(item => {
    // Super admin always has full access
    if (isSuperAdmin()) {
      return true;
    }
    
    // Check allowedRoles if specified
    if (item.allowedRoles) {
      if (!user?.role || !item.allowedRoles.includes(user.role)) {
        return false;
      }
    }
    
    // Check dynamic role permissions from org settings
    const rolePerms = currentOrg?.role_permissions;
    if (rolePerms && user?.role && rolePerms[user.role]) {
      const userRolePerms = rolePerms[user.role];
      // Handle new action-level format: { modulePath: ['view', 'add', ...] }
      if (typeof userRolePerms === 'object' && !Array.isArray(userRolePerms)) {
        const modulePerms = userRolePerms[item.path];
        // User has access if they have any action permissions (especially 'view')
        return Array.isArray(modulePerms) && modulePerms.length > 0;
      }
      // Handle legacy array format: ['/path1', '/path2', ...]
      if (Array.isArray(userRolePerms)) {
        return userRolePerms.includes(item.path);
      }
    }
    // Fallback to static permission checks if no dynamic permissions configured
    if (item.requiresSuperAdmin) return false;
    if (item.requiresAdmin && !canAdmin()) return false;
    if (item.requiresEdit && !canEdit()) return false;
    return true;
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-[#1a2744] text-white border-r border-[#1e3a5f] z-50 transform transition-all duration-300 ease-in-out lg:translate-x-0 flex flex-col ${
          collapsed ? 'w-16' : 'w-64'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="p-4 flex-shrink-0">
          {/* Mobile close button */}
          <div className="flex items-center justify-between lg:hidden mb-4">
            <div className="flex items-center gap-2">
              <KairosLogo size="sm" />
              {!collapsed && <span className="font-bold text-sm">Ocean Fishing Bissau</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Desktop header */}
          <div className={`hidden lg:flex items-center mb-6 ${collapsed ? 'justify-center' : 'gap-3 px-2'}`}>
            <KairosLogo size={collapsed ? 'sm' : 'md'} />
            {!collapsed && (
              <div>
                <h1 className="font-bold text-sm text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Ocean Fishing Bissau
                </h1>
                <p className="text-xs text-blue-300/70">{t('appTagline')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          <nav className="space-y-1">
            {filteredItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  title={collapsed ? item.label : undefined}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                    collapsed ? 'justify-center' : ''
                  } ${
                    isActive 
                      ? 'bg-white/15 text-white border-l-2 border-blue-300 -ml-0.5 pl-3.5 font-medium' 
                      : 'text-blue-200/70 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:flex flex-col p-3 border-t border-white/10 flex-shrink-0">
          {/* Version display */}
          {!collapsed && (
            <div className="text-[10px] text-blue-300/50 text-center mb-2 font-mono">
              {getVersionString('short')}
            </div>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onToggleCollapse}
            className={`w-full text-blue-200/70 hover:text-white hover:bg-white/10 ${collapsed ? 'justify-center' : 'justify-start'}`}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? (
              <PanelLeft className="w-4 h-4" />
            ) : (
              <>
                <PanelLeftClose className="w-4 h-4 mr-2" />
                <span className="text-xs">{t('common.collapse')}</span>
              </>
            )}
          </Button>
        </div>

        {/* Mobile version display */}
        <div className="lg:hidden p-3 border-t border-white/10 flex-shrink-0">
          <div className="text-[10px] text-blue-300/50 text-center font-mono">
            {getVersionString('short')}
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const { user, organizations, currentOrg, switchOrganization, logout } = useAuth();
  const { fiscalYears, selectedFY, switchFiscalYear, clearSelection, hasFiscalYears } = useFiscalYear();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b border-border px-4 lg:px-6 py-3" data-testid="header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 lg:gap-4">
          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Organization switcher - Only visible for super_admin with multiple organizations */}
          {user?.role === 'super_admin' && organizations.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="org-switcher">
                  <Building2 className="w-4 h-4 hidden sm:block" />
                  <span className="max-w-[100px] sm:max-w-[200px] truncate">{currentOrg?.name || 'Select Org'}</span>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[60] w-[250px]">
                {organizations.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => switchOrganization(org)}
                    className={currentOrg?.id === org.id ? 'bg-primary/10' : ''}
                    data-testid={`org-option-${org.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">{org.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {org.currency} | Rate: {org.base_exchange_rate.toLocaleString()}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Fiscal Year Selector */}
          {hasFiscalYears && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${
                    selectedFY?.status === 'closed' ? 'border-amber-500/50 text-amber-600' : 'border-emerald-500/50 text-emerald-600'
                  }`}
                  data-testid="fy-selector"
                >
                  <Calendar className="w-4 h-4 hidden sm:block" />
                  <span className="max-w-[120px] sm:max-w-[180px] truncate">
                    {selectedFY ? selectedFY.name : t('common.allPeriods')}
                  </span>
                  {selectedFY?.status === 'closed' ? (
                    <Lock className="w-3 h-3" />
                  ) : selectedFY ? (
                    <Unlock className="w-3 h-3" />
                  ) : null}
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[60] w-[280px]">
                <DropdownMenuItem
                  onClick={() => clearSelection()}
                  className={!selectedFY ? 'bg-primary/10' : ''}
                  data-testid="fy-option-all"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">All Periods</span>
                    <span className="text-xs text-muted-foreground">No fiscal year filter</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {fiscalYears.map((fy) => (
                  <DropdownMenuItem
                    key={fy.id}
                    onClick={() => switchFiscalYear(fy)}
                    className={selectedFY?.id === fy.id ? 'bg-primary/10' : ''}
                    data-testid={`fy-option-${fy.id}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex flex-col">
                        <span className="text-sm">{fy.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {fy.start_date} to {fy.end_date}
                        </span>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        fy.status === 'open' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {fy.status === 'open' ? 'Open' : 'Closed'}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          <LanguageSwitcher />
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1 sm:gap-2 px-2 sm:px-3" data-testid="user-menu">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-xs sm:text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{getRoleDisplayName(user.role)}</p>
                  </div>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[60]">
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground text-sm">{user.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-400" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();

  const toggleCollapse = () => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebarCollapsed', String(newValue));
      return newValue;
    });
  };

  // Global shortcuts: ?, /, Escape
  useKeyboardShortcuts([
    {
      key: '?', shift: true,
      action: () => setShortcutsOpen(true)
    },
    {
      key: '/',
      action: () => {
        const searchEl = document.querySelector('[data-testid*="search"] input, [data-testid*="search"], input[placeholder*="Search"], input[placeholder*="search"]');
        if (searchEl) searchEl.focus();
      }
    },
    {
      key: 'Escape', allowInInput: true,
      action: () => {
        const active = document.activeElement;
        if (active && active.tagName.toLowerCase() === 'input') {
          active.blur();
        }
      },
      preventDefault: false
    },
    {
      key: 'n', ctrl: true,
      action: () => {
        const addBtn = document.querySelector('[data-testid*="add-"][data-testid*="btn"]');
        if (addBtn) addBtn.click();
      },
      allowInInput: true
    },
    {
      key: 's', ctrl: true,
      action: () => {
        const saveBtn = document.querySelector('[data-testid*="save-"][data-testid*="btn"]:not([disabled])');
        if (saveBtn) saveBtn.click();
      },
      allowInInput: true
    }
  ]);

  // Sequence shortcuts: g+d, g+v, etc.
  useSequenceShortcut((seq, reset) => {
    const routes = {
      'gd': '/',
      'gv': '/vouchers',
      'ga': '/chart-of-accounts',
      'gc': '/customers',
      'gs': '/suppliers',
      'gn': '/cr-db-notes',
      'gi': '/inventory'
    };
    if (routes[seq]) {
      navigate(routes[seq]);
      reset();
    }
    // Reset if sequence is too long or doesn't start with 'g'
    if (seq.length >= 2 && !routes[seq]) reset();
  });

  const location = useLocation();
  const isPosPage = false;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapse}
      />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className={`p-4 lg:p-6 animate-fade-in ${isPosPage ? 'pb-4' : 'pb-20'}`} data-testid="main-content">
          {children}
        </main>
      </div>
      {!isPosPage && <AppFooter />}
      <ShortcutsHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
};

export default Layout;